import { DoubleArrowLeftIcon } from '@radix-ui/react-icons';
import { Box, Flex, Separator } from '@radix-ui/themes';
import { InboxDialog } from 'components/common/dialogs/inbox-dialog';
import { ErrorBoundary } from 'components/common/error-boundary';
import { CommonNavMenu } from 'components/common/layout/nav-menu';
import { AdminSections } from 'components/main/sidebar/admin-sections';
import { SidebarFooter } from 'components/main/sidebar/footer';
import { SidebarPitchLists } from 'components/main/sidebar/pitch-lists';
import { SidebarReminder } from 'components/main/sidebar/reminder';
import { UserSections } from 'components/main/sidebar/user-sections';
import env from 'config';
import { CookieKey, SidebarState } from 'enums/cookies.enums';
import { useNavSections } from 'hooks/useNavSections';
import { nextSidebarState } from 'interfaces/cookies/i-app.cookie';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { useMemo, useState } from 'react';
import { useCookiesStore } from 'stores/use-cookies-store';
import useMiscStore from 'stores/use-misc-store';

const SidebarSeparator = () => (
  <Box mb={RADIX.FLEX.GAP.MAIN_MENU}>
    <Separator mt="2" mb="2" size="4" />
  </Box>
);

export const Sidebar = () => {
  const app = useCookiesStore((s) => s.app);

  const expanded = app.sidebar_state === SidebarState.full;

  const toggleCallback = () => {
    useCookiesStore.getState().setCookie(CookieKey.app, {
      sidebar_state: nextSidebarState(app.sidebar_state),
    });
  };

  const { adminSections } = useNavSections();

  const hasAdmin = useMemo(
    () => adminSections && adminSections.filter((s) => !s.invisible).length > 0,
    [adminSections]
  );

  // store a ref to the "SidebarScrollable" container so we can pass it all the way down to the virtualized list components in FullMenu
  const [scrollParent, setScrollParent] = useState<HTMLDivElement | null>(null);

  return (
    <ErrorBoundary identifier="MainSidebar">
      <Flex
        data-identifier="SidebarWrapper"
        className={RADIX.VFLEX.WRAPPER}
        direction="column"
        // prevents content from resizing this thing
        overflow="hidden"
        p={RADIX.FLEX.PAD.MAIN}
      >
        <Flex
          mb="3"
          data-identifier="SidebarHeader"
          gap={RADIX.FLEX.GAP.SM}
          justify={expanded ? 'between' : 'start'}
          align="center"
        >
          <Box>
            {/* render as 2 different DOM elements to make sure it re-renders immediately */}
            {expanded ? (
              <img
                alt="Trajekt logo"
                title={env.getBuildStamp()}
                src={useMiscStore.getState().getThemeImgPath('logo.svg')}
                className="cursor-pointer select-none"
                onClick={toggleCallback}
                style={{
                  height: '32px',
                  width: 'auto',
                }}
              />
            ) : (
              <img
                alt="Trajekt icon"
                title={env.getBuildStamp()}
                src={useMiscStore.getState().getThemeImgPath('icon.svg')}
                className="cursor-pointer select-none"
                onClick={toggleCallback}
                style={{
                  height: '32px',
                  width: 'auto',
                }}
              />
            )}
          </Box>

          {expanded && (
            <Box>
              <DoubleArrowLeftIcon
                className="cursor-pointer"
                onClick={toggleCallback}
              />
            </Box>
          )}
        </Flex>

        <SidebarReminder expanded={expanded} />

        <Box
          data-identifier="SidebarScrollable"
          ref={setScrollParent}
          flexGrow="1"
          overflowY="scroll"
          overflowX="hidden"
          mb={RADIX.FLEX.GAP.MAIN_MENU}
          style={{
            scrollbarWidth: 'none',
          }}
        >
          <Box data-identifier="SidebarUserSections">
            <CommonNavMenu orientation="vertical">
              <UserSections />
            </CommonNavMenu>
          </Box>

          {hasAdmin && (
            <>
              <SidebarSeparator />

              <Box data-identifier="SidebarAdminSections">
                <CommonNavMenu orientation="vertical">
                  <AdminSections />
                </CommonNavMenu>
              </Box>
            </>
          )}

          <SidebarSeparator />

          {scrollParent && <SidebarPitchLists scrollParent={scrollParent} />}
        </Box>

        <SidebarSeparator />

        <Box className="SidebarFooter">
          <SidebarFooter />
        </Box>
      </Flex>

      <InboxDialog />
    </ErrorBoundary>
  );
};
